import Button from '@rio-cloud/rio-uikit/Button';
import { FormattedMessage } from 'react-intl';
import React, { useEffect, useState } from 'react';
import { reportErrorToSentry } from '../../../../../configuration/setup/sentry';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { getSelectedTag, getSelectedTagVersion, getTagUpdating } from '../../Tags.selector';
import type { Tag } from '../../types';
import { deleteTags, updateTag as updateTagThunk, fetchETag as fetchETagThunk } from '../../tagsThunks';
import AccentBar from '@rio-cloud/rio-uikit/AccentBar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';

export interface TagListDetailsFooterProps {
    newTagName: string;
}

export const TagListDetailsFooter = (props: TagListDetailsFooterProps) => {
    const { newTagName } = props;

    const dispatch = useAppDispatch();

    const deleteTag = (tagId: string) => dispatch(deleteTags({ tagIds: [tagId] }));
    const updateTag = (updatedTag: Tag, tagVersion: string) =>
        dispatch(updateTagThunk({ tag: updatedTag, tagVersion }));

    const selectedTag = useAppSelector(getSelectedTag);
    const selectedTagVersion = useAppSelector(getSelectedTagVersion);
    const tagUpdating = useAppSelector(getTagUpdating);

    const [showDeleteSingleTagDialog, setShowDeleteSingleTagDialog] = useState<boolean>(false);

    useEffect(() => {
        const fetchETag = (tagId: string) => dispatch(fetchETagThunk({ tagId }));
        if (selectedTag) {
            fetchETag(selectedTag.id);
        }
    }, [dispatch, selectedTag]);

    const deleteClick = () => {
        setShowDeleteSingleTagDialog(true);
    };

    const renderDeleteButton = () => (
        <Button
            autoTrackingKey='tags.tagDetails.deleteTagButton'
            bsStyle={Button.DANGER}
            variant={Button.VARIANT_LINK}
            disabled={false}
            onClick={deleteClick}
            data-cy='detail-view-delete-button'
        >
            <span className='rioglyph rioglyph-trash text-color-danger margin-right-5' />
            <span>
                <FormattedMessage id={'tags.tagDetails.deleteTagButton'} />
            </span>
        </Button>
    );

    const saveButtonDisabled = () =>
        newTagName.trim().length === 0 ||
        selectedTag?.name === newTagName ||
        tagUpdating ||
        selectedTagVersion === null;

    const submitButtonClick = () => {
        if (selectedTag !== null && selectedTagVersion !== null) {
            updateTag({ ...selectedTag, name: newTagName }, selectedTagVersion);
        } else {
            reportErrorToSentry(new Error('Could not update tag because the selected Tag or version is undefined'));
        }
    };

    const renderSubmitButton = () => (
        <Button
            autoTrackingKey='tags.tagDetails.submit'
            bsStyle={Button.PRIMARY}
            className={tagUpdating ? 'btn-loading' : undefined}
            disabled={saveButtonDisabled()}
            onClick={submitButtonClick}
            data-cy='detail-view-submit-button'
        >
            <FormattedMessage id={'tags.tagDetails.submit'} />
        </Button>
    );

    const onConfirmDelete = () => {
        if (selectedTag) {
            setShowDeleteSingleTagDialog(false);
            deleteTag(selectedTag.id);
        }
    };

    const onCancelDelete = () => {
        setShowDeleteSingleTagDialog(false);
    };

    const tagName = selectedTag?.name ?? '';

    return (
        <React.Fragment>
            <div className='btn-toolbar display-flex justify-content-between'>
                {renderDeleteButton()}
                {renderSubmitButton()}
            </div>
            <div data-cy='tags-delete-single-tag-confirmation-dialog'>
                <Dialog
                    show={showDeleteSingleTagDialog}
                    title={<FormattedMessage id={'tags.tagDetails.deleteConfirmationDialog.title'} />}
                    bsSize={'sm'}
                    onEsc={onCancelDelete}
                    body={
                        <>
                            <div>
                                <FormattedMessage
                                    id={'tags.tagDetails.deleteConfirmationDialog.content.text'}
                                    values={{
                                        tagName: tagName,
                                    }}
                                />
                            </div>
                            <div className={'margin-top-10'}>
                                <AccentBar color='bg-warning'>
                                    <div>
                                        <div className='text-medium margin-bottom-5'>
                                            <FormattedMessage
                                                id={'tags.tagDetails.deleteConfirmationDialog.content.accent.text'}
                                            />
                                        </div>
                                        <ul className='margin-bottom-0'>
                                            <li>
                                                <FormattedMessage
                                                    id={
                                                        'tags.tagDetails.deleteConfirmationDialog.content.accent.bullet1'
                                                    }
                                                />
                                            </li>
                                            <li>
                                                <FormattedMessage
                                                    id={
                                                        'tags.tagDetails.deleteConfirmationDialog.content.accent.bullet2'
                                                    }
                                                />
                                            </li>
                                        </ul>
                                    </div>
                                </AccentBar>
                            </div>
                        </>
                    }
                    footer={
                        <div className='btn-toolbar'>
                            <Button
                                autoTrackingKey='tags.tagDetails.deleteConfirmationDialog.button.cancel'
                                onClick={onCancelDelete}
                            >
                                <FormattedMessage id={'tags.tagDetails.deleteConfirmationDialog.button.cancel'} />
                            </Button>
                            <Button
                                autoTrackingKey='tags.tagDetails.deleteConfirmationDialog.button.confirm'
                                bsStyle={Button.WARNING}
                                onClick={onConfirmDelete}
                            >
                                <FormattedMessage id={'tags.tagDetails.deleteConfirmationDialog.button.confirm'} />
                            </Button>
                        </div>
                    }
                    useOverflow={true}
                />
            </div>
        </React.Fragment>
    );
};
